<template>
  <div class="app">
    <cl-sidebar v-model="isClosed">
      <template #company-logo="{ isClosed }">
        <div
          v-if="isRequestPending"
          class="tw-w-[154px] tw-h-[52px] tw-flex tw-justify-center tw-items-center"
        >
          <cl-spinner :size="'small'" class="tw-mr-2" />
        </div>
        <t-logo
          v-else
          @click.prevent="redirectBasedOnRole"
          :logo-src="logoSrc"
          v-show="!isClosed"
        />
      </template>
      <template #dropdown-select="{ isClosed }">
        <!-- TODO: fix assertion customer as UserAccount -->
        <t-nav-dropdown
          icon="cl-tabler-icon-users"
          data-test-id="navbar-select-customer"
          :loading="false"
          :is-closed="isClosed"
          :options="userAccountList ? userAccountList : []"
          :initial-selected="initialMsp ? initialMsp.uuid : undefined"
          data-clarity-mask="true"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="(customer) => selectCustomer(customer as UserAccount)"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          v-if="!loadingSolutions"
          :nav-menu-set-up="mspNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
      <template #info-section="{ isClosed }" v-if="isDefaultBranding">
        <img
          v-if="!isClosed"
          alt="CyberSentriq logo"
          :src="cyberSentriqLogoSrc"
        />
      </template>
    </cl-sidebar>
    <div class="app-content">
      <t-upper-nav />
      <main
        class="tw-overflow-y-aut tw-flex-1 tw-bg-light tw-p-4 max-lg:tw-ml-14"
      >
        <div class="tw-w-full">
          <read-only-role-banner :visible="isReadOnlyRoleVisible" level="msp" />

          <t-page-header
            :product-links="productLinks"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <router-view />
          <cyber-sentriq-announcement-modal
            v-if="isCybersentriqModalAvailable"
          />
        </div>
      </main>
      <cl-footer :msp-name="getOrganisationBranding.name" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import type { ComputedRef, Ref } from "vue";

import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import { TIER } from "@/constants";

import {
  usePrimaryNavItems,
  useEnabledSolutions,
  useRedirectBasedOnRole,
} from "@/composables";
import { UUID_SEGMENT_REGEX } from "@/constants/regex";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useFlag } from "@/composables/useFlags/useFlag";

import { NAV_ITEMS_MODE } from "@/constants";
import { ALL_SERVICES } from "@/utils/services";

import TSideNav from "@/components/TSideNav";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { useI18n } from "vue-i18n";
import type { OrganisationBranding } from "@/types/organisatio";
import type { DataItem } from "@/components/TSideNav/types";
import type { ProductLink } from "@/components/TPageHeader/TPageHeader.types";
import { getAccountShortName } from "@/utils/getAccountShortName";
import CyberSentriqAnnouncementModal from "@/components/CyberSentriqAnnouncementModal";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import { storeToRefs } from "pinia";
import { useSidebarStore } from "@/store/sidebar";
import { selectCustomerHandler } from "./selectCustomerHandler";
import { isNavItemDisabled } from "@/utils/navItemHelpers";

type NavItem = {
  order: number;
  label: string;
  icon: string;
  id: string;
  dataTestId: string;
  groupBy: string;
  solutionId?: string;
  children?: NavItem[];
  tooltip?:
    | {
        content: string;
        placement: string;
      }
    | undefined;
  disabled?: boolean;
  users?: string[];
};

type UserAccount = {
  uuid: string;
  id: string;
  name: string;
  short: string;
  active: boolean;
  allCustomers: boolean;
  allMsps?: boolean;
  placeholder: string;
  organisation_type: string;
};

type UserLink = {
  title: string;
  link: string;
  target: string;
  icon: string;
};

type SearchParams = {
  filter: string;
  results: number;
};

type AuthUser = {
  uuid: string;
  role: string;
};

type SelectedAccount = {
  type_name: string;
  uuid: string;
};

type SelectedMsp = {
  name: string;
  uuid: string;
};

type EnabledSolution = {
  service: {
    uuid: string;
  };
  solution_uuid: string;
};

type Account = {
  uuid: string;
  name: string;
  short: string;
  active: boolean;
  allCustomers: boolean;
  placeholder: string;
  organisation_type: string;
};

const store = useStore();
const router = useRouter();
const route = useRoute();

const { navItems } = usePrimaryNavItems();
const { enabledSolutions } = useEnabledSolutions();
const { mspGlobalOrganisationId } = useGlobalOrganisationID();
const { isFeatureAvailable: _isFeatureAvailable } = useFlag("FEATURE_TOUR");
const redirectBasedOnRole = useRedirectBasedOnRole();
const { setRequestStatus, isRequestPending } = useRequestStatus();
const { isFeatureAvailable: isCybersentriqModalAvailable } = useFlag(
  "CYBERSENTRIQ_ANNOUNCEMENT_MODAL",
);

const mspNavItems = ref<NavItem[]>([]);
watch(
  () => [
    navItems.get(NAV_ITEMS_MODE.MSP)?.length,
    enabledSolutions.value?.length,
  ],
  () => {
    mspNavItems.value = navItems.get(NAV_ITEMS_MODE.MSP) || [];
  },
  { immediate: true },
);
const loadingSolutions: Ref<boolean> = ref(true);

const scopeMspId = (id: string, orgUuid?: string) => {
  if (!orgUuid || !id?.startsWith("/msp")) return id;
  const segments = id.split("/").filter(Boolean);
  if (segments[0] !== "msp") return id;
  const remainder = segments.slice(1);
  if (remainder[0] && UUID_SEGMENT_REGEX.test(remainder[0])) {
    remainder.shift();
  }
  const suffix = remainder.length ? `/${remainder.join("/")}` : "";
  return `/msp/${orgUuid}${suffix}`;
};

const getSolutions = async (uuid: string): Promise<void> => {
  await store.dispatch("solutions/getSolutions", {
    uuid,
  });
};

const selectedMsp: ComputedRef<SelectedMsp | null> = computed(
  () => store.getters["authentication/selectedMsp"],
);
const selectedAccount: ComputedRef<SelectedAccount | null> = computed(
  () => store.getters["authentication/selectedAccount"],
);

const isDefaultBranding = computed<boolean>(() => {
  return !!(
    !getOrganisationBranding.value.logo_dark &&
    getOrganisationBranding.value.name === DEFAULT_BRANDING_NAME
  );
});

onMounted(async () => {
  setRequestStatus({ type: PENDING });
  await store.dispatch("branding/getBranding", {
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });
  setRequestStatus({ type: READY });
});

onBeforeMount(async () => {
  store.dispatch("authentication/setCustomer", null);
  const orgUuid = route.params?.orgUuid as string | undefined;
  if (orgUuid) {
    await store.dispatch("authentication/setMspUser", {
      uuid: orgUuid,
      name: selectedMsp.value?.name || "",
    });
    store.dispatch("pageMeta/setMSP", selectedMsp.value?.name || "");
  }
  if (!mspGlobalOrganisationId.value) {
    return;
  }

  try {
    loadingSolutions.value = true;

    store.dispatch("solutions/resetState");

    await getSolutions(mspGlobalOrganisationId.value);
  } finally {
    if (route.meta.solutionId) {
      let isSolutionDisabled = false;

      isSolutionDisabled = !enabledSolutions.value.some(
        (solution: EnabledSolution) =>
          solution.solution_uuid === route.meta.solutionId,
      );

      if (isSolutionDisabled) {
        await router.replace({
          name: "msp-root",
          params: { orgUuid: mspGlobalOrganisationId.value },
        });
        router.go(0);
      }
    }

    loadingSolutions.value = false;
  }
});

watch(
  () => route.params.orgUuid,
  async (orgUuid) => {
    if (orgUuid && orgUuid !== selectedMsp.value?.uuid) {
      await store.dispatch("authentication/setMspUser", {
        uuid: orgUuid as string,
        name: selectedMsp.value?.name || "",
      });
      store.dispatch("pageMeta/setMSP", selectedMsp.value?.name || "");
    }
  },
  { immediate: true },
);

const userAccountList: Ref<UserAccount[] | null> = ref(null);
const initialMsp: Ref<SelectedMsp | null> = ref(null);
const tier = TIER.msps;
const params: Ref<SearchParams> = ref({
  filter: "",
  results: 20,
});
const productLinks: Ref<ProductLink[]> = ref([]);
const userLinks: Ref<UserLink[]> = ref([]);

const { t } = useI18n();
const sidebarStore = useSidebarStore();
const { isClosed } = storeToRefs(sidebarStore);

const authUser: ComputedRef<AuthUser> = computed(
  () => store.getters["authentication/authUser"],
);
const dropDownList: ComputedRef<Account[]> = computed(
  () => store.getters["dropDown/dropDownList"],
);
const getOrganisationBranding: ComputedRef<OrganisationBranding> = computed(
  () => store.getters["branding/getOrganisationBranding"],
);

const setParentPath = (path: string) =>
  store.dispatch("pageMeta/setParentPath", path);
const setMSP = (msp: string) => store.dispatch("pageMeta/setMSP", msp);
const setCustomer = (customer: unknown) =>
  store.dispatch("authentication/setCustomer", customer);
const getDropDownList = (params: unknown) =>
  store.dispatch("dropDown/getDropDownList", params);
const resetState = () => store.dispatch("dropDown/resetState");

const isReadOnlyRoleVisible: ComputedRef<boolean> = computed(() => {
  return authUser.value?.role === "admin-ro";
});

const separatedByGroup = (mspArray: DataItem[]) => {
  const mspByGroup: Record<string, DataItem[]> = {};
  mspArray.forEach((item) => {
    if (mspByGroup[item.groupBy]) {
      mspByGroup[item.groupBy].push(item);
    } else {
      mspByGroup[item.groupBy] = [item];
    }
  });
  return mspByGroup;
};

const mspNavMenuSetUp: ComputedRef<Record<string, DataItem[]>> = computed(
  () => {
    const orgUuid =
      (route.params?.orgUuid as string) ||
      selectedMsp.value?.uuid ||
      selectedAccount.value?.uuid;

    const items = mspNavItems.value
      .map((item: NavItem) => {
        const serviceSettings = ALL_SERVICES.find(
          (service: { uuid: string }) => service.uuid === item.solutionId,
        );

        const isSolutionDisabled = ref(false);
        let tooltip: { content: string; placement: string } | undefined =
          undefined;

        if (item.solutionId) {
          isSolutionDisabled.value = !enabledSolutions.value.some(
            (solution: EnabledSolution) =>
              solution.service.uuid === item.solutionId,
          );
        }

        // Instead of hiding, the disabled services will appear for MSPs:
        // https://gitlab.titanhq.com/titanhq/dev/scrum/team-platfarmers/-/issues/2097
        if (isSolutionDisabled.value && serviceSettings) {
          tooltip = {
            content: t(serviceSettings.tooltipLabelUnavailable),
            placement: "top",
          };
        }
        return {
          ...item,
          id: scopeMspId(item.id, orgUuid),
          tooltip,
          disabled: isSolutionDisabled.value,
          children: item.children
            ?.filter(
              (child: NavItem) =>
                !isNavItemDisabled({
                  navItem: child,
                  enabledSolutions: enabledSolutions.value,
                  parentDisabled: isSolutionDisabled.value,
                  mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                }),
            )
            .map((child: NavItem) => {
              return {
                ...child,
                id: scopeMspId(child.id, orgUuid),
                disabled: isSolutionDisabled.value,
                children: child.children?.filter(
                  (grandChild: NavItem) =>
                    !isNavItemDisabled({
                      navItem: grandChild,
                      enabledSolutions: enabledSolutions.value,
                      parentDisabled: false,
                      mspGlobalOrganisationId: mspGlobalOrganisationId.value,
                    }),
                ),
              };
            }),
        };
      })
      .filter(Boolean);

    // TODO: fix type assertion as DataItem[]
    return separatedByGroup(items as unknown as DataItem[]);
  },
);

// Recompute when nav registry or enabled solutions change (handles late registrations)
watch(
  () => [mspNavItems.value.length, enabledSolutions.value?.length],
  () => {
    // no-op body; dependency forces computed mspNavMenuSetUp to recompute
  },
);

const logoSrc: ComputedRef<string> = computed(() => {
  if (getOrganisationBranding.value?.logo_dark) {
    return `${import.meta.env.VITE_MEDIA_URL}/${getOrganisationBranding.value.logo_dark}`;
  }
  return new URL("@/assets/branding/Titan_Logo.svg", import.meta.url).href;
});

const cyberSentriqLogoSrc = new URL(
  "@/assets/branding/powered-by-cybersentriq.svg",
  import.meta.url,
).href;

const selectCustomer = async (customer: UserAccount): Promise<void> => {
  await selectCustomerHandler({
    customer,
    selectedAccount: selectedAccount.value,
    setCustomer,
    router,
    routePath: route.path,
    store,
  });
};

const setDropDownList = async (): Promise<void> => {
  try {
    resetState();
    await getDropDownList({
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: params.value,
    });
  } finally {
    userAccountList.value = cleanAccounts(dropDownList.value);
  }
};

const itemBeingSearched = async (item: string): Promise<void> => {
  try {
    const searchParams = {
      filter: item,
      results: 20,
    };
    resetState();
    await getDropDownList({
      globalOrganisationID:
        selectedMsp.value?.uuid || selectedAccount.value?.uuid,
      params: searchParams,
    });
  } catch {
    // Handled in axios interceptor
  }
};

const cleanAccounts = (accounts: Account[]): UserAccount[] => {
  const allAccounts = accounts
    .map((acct: Account) => {
      return {
        ...acct,
        id: acct.uuid,
        name: acct.name || "",
        short: getAccountShortName(acct.name || ""),
        active: false,
        allCustomers: false,
        placeholder: "Search Customers",
      };
    })
    .filter(
      (res: Account) =>
        res.organisation_type === "Customer" ||
        res.organisation_type === "Distributor",
    )
    .sort((a: UserAccount, b: UserAccount) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
          ? -1
          : 0,
    );

  if (selectedAccount.value?.type_name === "System") {
    const isViewingChildMsp = Boolean(selectedMsp.value?.uuid);
    const otherAccounts = [
      {
        uuid: "",
        id: "",
        name: isViewingChildMsp
          ? t("menu-item-all-customers")
          : t("menu-item-all-msps"),
        short: "AM",
        active: false,
        allMsps: !isViewingChildMsp,
        allCustomers: isViewingChildMsp,
        placeholder: isViewingChildMsp ? "Search Customers" : "Search MSPs",
        organisation_type: isViewingChildMsp ? "Customer" : "MSP",
      },
    ];
    return [...otherAccounts, ...allAccounts];
  } else {
    let otherAccounts = [
      {
        uuid: "",
        id: "",
        name: "All Customers",
        short: "AM",
        active: false,
        allMsps: false,
        allCustomers: true,
        placeholder: "Search Customers",
        organisation_type: "Customer",
      },
    ];
    return [...otherAccounts, ...allAccounts];
  }
};

watch(
  dropDownList,
  () => {
    userAccountList.value = cleanAccounts(dropDownList.value);
  },
  { deep: true, immediate: true },
);

watch(selectedMsp, async (newMsp, oldMsp) => {
  if (newMsp && newMsp.uuid !== oldMsp?.uuid) {
    loadingSolutions.value = true;
    store.dispatch("solutions/resetState");
    await getSolutions(newMsp.uuid);
    loadingSolutions.value = false;
  }
});

const initializeLayout = (): void => {
  let parentPath = "";

  if (selectedAccount.value?.type_name === "System") {
    parentPath += "/system";
  }

  setParentPath(parentPath);
  setMSP(selectedMsp.value?.name || "");
};

onBeforeMount(initializeLayout);
</script>
